import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { RefDataService } from '@services/ref-data.service';
import { Subject } from 'rxjs';
import { FormArray, FormBuilder, FormControl, FormGroup, ɵRawValue } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ServiceFormValue, ServicesConfs } from '../../models/client.model';
import { DataRef, DataRefsByKey, LocalizedKeyValue } from '@shared/models/dataref.model';
import { placementIdRequiredValidator } from './service-management-validator';
import { takeUntil } from 'rxjs/operators';
import { KeyValue } from '@angular/common';
import { NotificationService } from '@services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { MatSelectChange } from '@angular/material/select';

export interface ServiceForm {
  key: FormControl<string>;
  isActive: FormControl<boolean>;
  isChargeable: FormControl<boolean>;
  placementId: FormControl<string>;
}

export interface MarketServiceValueChange {
  marketName: string;
  servicesConfs: ServicesConfs;
}

const PREMIUM_RECIPES = 'PREMIUM_RECIPES';
const NOTEBOOKS = 'NOTEBOOKS';
const SHOPPING_LIST = 'SHOPPING_LIST';

@Component({
  selector: 'app-service-management',
  templateUrl: './service-management.component.html',
  styleUrls: ['./service-management.component.scss'],
  standalone: false,
})
export class ServiceManagementComponent implements OnInit, OnDestroy, OnChanges {
  @Output() serviceFormValues = new EventEmitter<{ marketName: string; servicesConfs: Partial<ServicesConfs> }>();
  @Output() formStatusChanged = new EventEmitter<boolean>();
  @Input() marketName: string;
  @Input() servicesConfs: ServicesConfs | undefined;
  digitalServicesList: LocalizedKeyValue[] = [];
  placementIdsList: KeyValue<string, string>[];
  servicesFormArray: FormArray<FormGroup<ServiceForm>> = new FormArray<FormGroup<ServiceForm>>([]);
  displayServicesSelected: LocalizedKeyValue[] = [];
  selectedServices: ServiceFormValue[] = [];
  allServicesList: string[] = [];
  defaultServices = [NOTEBOOKS, SHOPPING_LIST];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly refDataService: RefDataService,
    private readonly fb: FormBuilder,
    private readonly notificationService: NotificationService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.getDataRefs();
    this.toggleServices();
    if (this.servicesConfs && this.servicesConfs.isEnabled === undefined) {
      this.servicesConfs.isEnabled = false;
    }

    this.servicesFormArray.statusChanges.subscribe((status) => {
      this.formStatusChanged.emit(status === 'INVALID');
    });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.servicesFormArray.clear();
    this.servicesConfs?.digitalServices?.forEach((service) => this.servicesFormArray.push(this.createServiceFormGroup(service)));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectionChange(event: MatSelectChange): void {
    // Retrieves the list of services selected in the user interface
    const selectedServices: ServiceFormValue[] = event.value;

    // Gets the current list of form values
    const currentFormValues = this.servicesFormArray.getRawValue();

    // Searches for the "PREMIUM_RECIPES" service in the current list
    const premiumRecipe = currentFormValues.find((value) => value.key === PREMIUM_RECIPES);

    // Resets the services form array
    this.servicesFormArray.clear();

    // If the "PREMIUM_RECIPES" service exists, adds it first to the services form array
    if (premiumRecipe) {
      this.servicesFormArray.push(
        this.createServiceFormGroup({
          key: premiumRecipe.key,
          isActive: premiumRecipe.isActive,
          isChargeable: premiumRecipe.isChargeable,
          placementId: premiumRecipe.placementId,
        }),
      );
    }

    // For each current form value, checks if the corresponding service is selected
    // If so, and if all necessary fields are present, adds the service to the services form array
    currentFormValues.forEach((formValue) => {
      const isSelected = selectedServices.some((service) => service.key === formValue.key);

      if (isSelected || formValue.key === NOTEBOOKS || formValue.key === SHOPPING_LIST) {
        if (
          formValue.key &&
          formValue.isActive !== undefined &&
          formValue.isChargeable !== undefined &&
          formValue.placementId !== undefined
        ) {
          this.servicesFormArray.push(this.createServiceFormGroup(formValue));
        }
      }
    });

    // For each selected service, checks if the service already exists in the current form values
    // If not, adds the service with default values to the services form array
    selectedServices.forEach((service) => {
      const serviceIndex = currentFormValues.findIndex((value) => value.key === service.key);

      if (serviceIndex === -1) {
        this.servicesFormArray.push(
          this.createServiceFormGroup({
            key: service.key,
            isActive: false,
            isChargeable: false,
            placementId: '',
          }),
        );
      }
    });

    // Checks for the presence of the "PREMIUM_RECIPES" service
    this.checkRecipePremium();
    this.emitUpdatedServices();
  }

  activateServices(event: MatCheckboxChange): void {
    this.servicesConfs ??= { isEnabled: false, digitalServices: [] };

    this.servicesConfs.isEnabled = event.checked;

    this.serviceFormValues.emit({
      marketName: this.marketName,
      servicesConfs: {
        isEnabled: event.checked,
      },
    });
    if (event.checked) {
      this.addDefaultServices();
      this.checkAndNotifyDefaultServices();
    } else {
      this.removeDefaultServices();
    }

    this.toggleServices();
  }

  onFormServiceRowChange(): void {
    this.checkRecipePremium();
    this.emitUpdatedServices();
  }

  private checkRecipePremium(): void {
    const premiumRecipesIndex = this.servicesFormArray.controls.findIndex((control) => control.get('key')?.value === PREMIUM_RECIPES);
    const hasPremiumRecipeService = premiumRecipesIndex !== -1;
    const hasChargeableService = this.servicesFormArray.controls.some(
      (control) => control.get('isChargeable')?.value && control.get('key')?.value !== PREMIUM_RECIPES,
    );

    if (!hasPremiumRecipeService && hasChargeableService) {
      this.servicesFormArray.insert(
        0,
        this.createServiceFormGroup({
          key: PREMIUM_RECIPES,
          isActive: true,
          isChargeable: true,
          placementId: '',
        }),
      );
      return;
    }

    if (hasPremiumRecipeService && !hasChargeableService) {
      this.servicesFormArray.removeAt(premiumRecipesIndex);
    }
  }

  private createServiceFormGroup(service: ɵRawValue<FormGroup<ServiceForm>>): FormGroup {
    const isRecipe = service.key === PREMIUM_RECIPES;
    const nonDisableKeys = [PREMIUM_RECIPES, SHOPPING_LIST, NOTEBOOKS];

    const group = this.fb.group(
      {
        key: [service.key],
        isActive: [{ value: service.isActive || isRecipe, disabled: isRecipe }],
        isChargeable: [{ value: service.isChargeable || isRecipe, disabled: isRecipe }],
        placementId: [service.placementId],
      },
      { validators: placementIdRequiredValidator },
    );

    // Désactivation globale si les services ne sont pas activés et si ce n'est pas 'PREMIUM_RECIPES'
    if (this.servicesConfs && !this.servicesConfs.isEnabled && !nonDisableKeys.includes(service.key)) {
      group.disable({ emitEvent: false });
    }

    if ([NOTEBOOKS, SHOPPING_LIST].includes(service.key)) {
      group.get('isActive')?.disable({ emitEvent: false });
    }

    return group;
  }

  private toggleServices(): void {
    if (this.servicesConfs?.isEnabled !== undefined) {
      this.servicesFormArray.controls.forEach((group: FormGroup<ServiceForm>) => {
        const serviceKey = group.get('key')?.value;
        if (serviceKey) {
          // Gestion de 'PREMIUM_RECIPES'
          if (serviceKey === PREMIUM_RECIPES) {
            if (this.servicesConfs?.isEnabled) {
              group.get('placementId')?.enable({ emitEvent: false });
            } else {
              group.get('placementId')?.disable({ emitEvent: false });
            }
          } else if (![NOTEBOOKS, SHOPPING_LIST].includes(serviceKey)) {
            if (this.servicesConfs?.isEnabled) {
              group.enable({ emitEvent: false });
            } else {
              group.disable({ emitEvent: false });
            }
          }
        }
      });
    }
  }

  private getDataRefs(): void {
    this.refDataService
      .getByKey('digitalServices', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allServices: DataRefsByKey<DataRef>) => {
        this.allServicesList = Object.keys(allServices);
        this.digitalServicesList = this.refDataService
          .translateReference(this.refDataService.toArray(allServices))
          .filter((service: { key: string }) => ![PREMIUM_RECIPES, NOTEBOOKS, SHOPPING_LIST].includes(service.key));
        if (this.servicesConfs) {
          this.displayServicesSelected = this.servicesConfs.digitalServices
            .map((digitalService: ServiceFormValue) => digitalService.key)
            .map((key: string) => this.digitalServicesList?.find((service) => service.key === key))
            .filter((service): service is LocalizedKeyValue => !!service);
        }
      });

    this.refDataService
      .getByKey('placementIds', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allPlacementIds) => {
        this.placementIdsList = Object.values(allPlacementIds);
      });
  }

  private addDefaultServices(): void {
    const premiumRecipesIndex = this.findPremiumRecipesIndex();

    this.defaultServices.forEach((serviceKey) => {
      if (!this.isServicePresent(serviceKey)) {
        const newServiceGroup = this.createServiceFormGroup({
          key: serviceKey,
          isActive: true,
          isChargeable: false,
          placementId: '',
        });

        const insertIndex = premiumRecipesIndex >= 0 ? premiumRecipesIndex + 1 : 0;
        this.servicesFormArray.insert(insertIndex, newServiceGroup);
      }
    });
    this.emitUpdatedServices();
  }

  private findPremiumRecipesIndex(): number {
    return this.servicesFormArray.controls.findIndex((control) => control.get('key')?.value === PREMIUM_RECIPES);
  }

  private isServicePresent(serviceKey: string): boolean {
    return this.servicesFormArray.controls.some((control) => control.get('key')?.value === serviceKey);
  }

  private removeDefaultServices(): void {
    this.defaultServices.forEach((serviceKey) => {
      const serviceIndex = this.servicesFormArray.controls.findIndex((control) => control.get('key')?.value === serviceKey);

      if (serviceIndex !== -1) {
        this.servicesFormArray.removeAt(serviceIndex);
      }
    });
    this.emitUpdatedServices();
  }

  private checkAndNotifyDefaultServices(): void {
    const missingServices = this.defaultServices.filter((service) => !this.allServicesList.includes(service));
    if (missingServices.length > 0) {
      const missingServicesString = missingServices.join(', ');
      const warningMessage = this.translateService.instant('SERVICES.MISSING_DEFAULT_SERVICES', { services: missingServicesString });
      this.notificationService.sendWarning(warningMessage);
    }
  }

  private emitUpdatedServices(): void {
    const updatedServices = this.servicesFormArray.getRawValue();
    this.serviceFormValues.emit({
      marketName: this.marketName,
      servicesConfs: {
        isEnabled: this.servicesConfs?.isEnabled,
        digitalServices: updatedServices,
      },
    });
  }
}
